/* @img-migrated */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Calendar, MapPin, Clock, Star, Upload, X, Save, Eye,
  Users, CheckCircle2, DollarSign, Camera, Image, Link2, FileText, UserCheck,
  XCircle, ExternalLink, BarChart3, Video, Newspaper, Sparkles, FileUp, RotateCcw, Mail
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { EventPDFImportDialog } from '../../components/EventPDFImportDialog';
import AdminModal from '../../components/AdminModal';
import AIImageGenerateModal from '../../components/admin/AIImageGenerateModal';
import { useAutoDraft } from '../../hooks/useAutoDraft';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = {
  title: '', description: '', date: '', time: '', end_date: '', end_time: '',
  location: '',
  category: 'general', is_featured: false, is_member_event: false, is_external: false,
  image_url: '', flyer_url: '', recap: '', recap_summary: '', recap_youtube_url: '',
  recap_published: false, gallery_images: [], max_capacity: '',
  rsvp_open: true, payment_link: '', payment_platform: '', payment_status_notes: '',
  ticket_url: '', info_url: '', video_url: '', external_url: '',
};

const TABS = [
  { key: 'upcoming', label: 'Upcoming Events', icon: Calendar },
  { key: 'past', label: 'Past Events', icon: Clock },
  { key: 'recaps', label: 'Event Recaps', icon: FileText },
  { key: 'rsvp', label: 'RSVP / Attendance', icon: Users },
  { key: 'payments', label: 'Payments / Sales', icon: DollarSign },
  { key: 'gallery', label: 'Media Gallery', icon: Camera },
];

const PAYMENT_PLATFORMS = [
  { value: '', label: 'None' },
  { value: 'zeffy', label: 'Zeffy' },
  { value: 'square', label: 'Square' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'other', label: 'Other' },
];

const EventsManagementPage = () => {
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState('upcoming');
  const [showEditor, setShowEditor] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({});
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [eventStats, setEventStats] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [aiImageOpen, setAiImageOpen] = useState(false);

  // --- Auto-save draft (scoped per event id) ---
  const draftKey = showEditor
    ? `dccf_draft_event_${editingEvent?.id || 'new'}`
    : null;
  const { restored: restoredDraft, clearDraft } = useAutoDraft(draftKey, formData, {
    enabled: showEditor,
    debounceMs: 600,
  });
  const dirty = useMemo(() => {
    if (!showEditor || !initialSnapshot) return false;
    try { return JSON.stringify(formData) !== JSON.stringify(initialSnapshot); } catch { return false; }
  }, [formData, initialSnapshot, showEditor]);
  useUnsavedChanges(dirty);

  useEffect(() => { fetchEvents(); ensureRecapDrafts(); }, []);  

  // Per-user preference for auto-publishing event recaps (bypass Comms approval).
  // Stored in localStorage so the toggle persists across sessions.
  const [autoPublishRecaps, setAutoPublishRecaps] = useState(() => {
    try { return localStorage.getItem('dccf_auto_publish_recaps') === 'true'; } catch { return false; }
  });
  const toggleAutoPublish = (val) => {
    setAutoPublishRecaps(val);
    try { localStorage.setItem('dccf_auto_publish_recaps', val ? 'true' : 'false'); } catch { /* */ }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`, { withCredentials: true });
      setEvents(res.data);
    } catch { /* */ }
  };

  /**
   * Auto-create recap blog drafts for every past event missing one.
   * Safe: backend endpoint is idempotent — only creates missing ones.
   * Passes `?publish=true` if the user has opted into auto-publish.
   */
  const ensureRecapDrafts = async () => {
    try {
      const url = `${API}/admin/events/ensure-recap-drafts${autoPublishRecaps ? '?publish=true' : ''}`;
      const res = await axios.post(url, {}, { withCredentials: true });
      if (res.data?.created_count > 0) {
        const msg = autoPublishRecaps
          ? `${res.data.created_count} recap${res.data.created_count !== 1 ? 's' : ''} published`
          : `${res.data.created_count} recap draft${res.data.created_count !== 1 ? 's' : ''} auto-created`;
        toast.success(msg);
        fetchEvents();
      }
    } catch { /* silent — not critical */ }
  };

  const handleGenerateRecap = async (eventId) => {
    try {
      const url = `${API}/admin/events/${eventId}/generate-recap${autoPublishRecaps ? '?publish=true' : ''}`;
      const res = await axios.post(url, {}, { withCredentials: true });
      toast.success(autoPublishRecaps ? 'Recap published to News' : 'Recap draft ready — edit it in Blog & News');
      setEvents(prev => prev.map(e => e.id === eventId ? { ...e, recap_blog_id: res.data.recap_blog_id, recap_published: res.data.is_published } : e));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate recap');
    }
  };

  /** One-click publish/unpublish for an event's recap — events team bypasses approval. */
  const handlePublishRecap = async (eventId, publish = true) => {
    try {
      const res = await axios.post(
        `${API}/admin/events/${eventId}/publish-recap`,
        { is_published: publish },
        { withCredentials: true }
      );
      toast.success(publish ? 'Recap published to News' : 'Recap moved back to draft');
      setEvents(prev => prev.map(e => e.id === eventId
        ? { ...e, recap_blog_id: res.data.blog_id, recap_published: res.data.is_published }
        : e
      ));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update recap');
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcomingEvents = events.filter(e => e.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const pastEvents = events.filter(e => e.date < today).sort((a, b) => b.date.localeCompare(a.date));
  const recapEvents = events.filter(e => e.recap_blog_id || e.recap_published || e.recap || e.recap_summary);

  const openEditor = (event = null) => {
    let next;
    if (event) {
      setEditingEvent(event);
      next = {
        title: event.title || '', description: event.description || '',
        date: event.date || '', time: event.time || '',
        end_date: event.end_date || '', end_time: event.end_time || '',
        location: event.location || '',
        category: event.category || 'general', is_featured: event.is_featured || false,
        is_member_event: event.is_member_event || false,
        is_external: event.is_external || false,
        image_url: event.image_url || '', flyer_url: event.flyer_url || '',
        recap: event.recap || '', recap_summary: event.recap_summary || '',
        recap_youtube_url: event.recap_youtube_url || '', recap_published: event.recap_published || false,
        gallery_images: event.gallery_images || [], max_capacity: event.max_capacity || '',
        rsvp_open: event.rsvp_open !== false, payment_link: event.payment_link || '',
        payment_platform: event.payment_platform || '', payment_status_notes: event.payment_status_notes || '',
        ticket_url: event.ticket_url || '', info_url: event.info_url || '',
        video_url: event.video_url || '', external_url: event.external_url || '',
      };
    } else {
      setEditingEvent(null);
      next = { ...EMPTY_FORM };
    }
    setFormData(next);
    setInitialSnapshot(next);
    setShowEditor(true);
  };

  const closeEditor = () => {
    // Draft is preserved so the user can recover their work by reopening the editor.
    // The draft is only cleared explicitly via the restore banner's "Discard" button
    // or automatically after a successful save.
    setShowEditor(false);
    setInitialSnapshot(null);
  };

  const restoreDraftToForm = () => {
    if (restoredDraft?.data) {
      setFormData(restoredDraft.data);
      toast.success('Draft restored — your progress is saved');
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.date || !formData.time || !formData.location) {
      toast.error('Title, date, time, and location are required'); return;
    }
    setSaving(true);
    const savingToastId = toast.loading('Saving your work...');
    try {
      const payload = { ...formData, max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null };
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, payload, { withCredentials: true });
        toast.success('Event updated', { id: savingToastId });
      } else {
        await axios.post(`${API}/events`, payload, { withCredentials: true });
        toast.success('Event created', { id: savingToastId });
      }
      clearDraft();
      setShowEditor(false);
      setInitialSnapshot(null);
      fetchEvents();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        // Axios interceptor already attempted a refresh — if we still got 401, session is truly gone
        toast.error('Session ended — your progress is saved. Please log in again to continue.', { id: savingToastId, duration: 6000 });
      } else {
        toast.error(err.response?.data?.detail || 'Save failed — your work is safe, try again.', { id: savingToastId });
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API}/events/${id}`, { withCredentials: true });
      toast.success('Event deleted');
      fetchEvents();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleNewsletterOptIn = async (event) => {
    const next = !event.include_in_newsletter;
    try {
      await axios.patch(
        `${API}/admin/newsletter-opt-in/events/${event.id}`,
        { include_in_newsletter: next },
        { withCredentials: true }
      );
      toast.success(next ? 'Added to newsletter pipeline' : 'Removed from newsletter pipeline');
      fetchEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update newsletter flag');
    }
  };

  const uploadImage = async (eventId, type, file) => {
    if (!file) return;
    setUploading(prev => ({ ...prev, [type]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const endpoint = type === 'flyer' ? 'flyer' : type === 'cover' ? 'cover' : 'gallery';
      const res = await axios.post(`${API}/events/${eventId}/${endpoint}`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (type === 'flyer') setFormData(prev => ({ ...prev, flyer_url: res.data.flyer_url }));
      else if (type === 'cover') setFormData(prev => ({ ...prev, image_url: res.data.image_url }));
      else setFormData(prev => ({ ...prev, gallery_images: [...(prev.gallery_images || []), res.data.image_url] }));
      toast.success('Uploaded');
      fetchEvents();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(prev => ({ ...prev, [type]: false })); }
  };

  const loadRegistrations = async (eventId) => {
    setSelectedEventId(eventId);
    try {
      const [regsRes, statsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/registrations`, { withCredentials: true }),
        axios.get(`${API}/admin/events/${eventId}/stats`, { withCredentials: true }),
      ]);
      setRegistrations(regsRes.data);
      setEventStats(statsRes.data);
    } catch { toast.error('Failed to load registrations'); }
  };

  const toggleAttendance = async (eventId, regId, attended) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/registrations/${regId}/status`, { attended }, { withCredentials: true });
      loadRegistrations(eventId);
    } catch { toast.error('Failed to update'); }
  };

  const updatePaymentStatus = async (eventId, regId, status) => {
    try {
      await axios.put(`${API}/admin/events/${eventId}/registrations/${regId}/status`, { payment_status: status }, { withCredentials: true });
      loadRegistrations(eventId);
    } catch { toast.error('Failed to update'); }
  };

  const inputClass = "w-full border border-slate-200 rounded-sm p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002249]/20";

  /* ── Event Card for lists ── */
  const EventCard = ({ event, showRecapBadge = false }) => {
    const isPast = event.date < today;
    const hasRecapDraft = !!event.recap_blog_id;
    return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-3 sm:p-4 bg-white border border-slate-200 rounded-sm hover:border-slate-300 transition-colors" data-testid={`event-card-${event.id}`}>
      <div className="flex items-start gap-3 flex-1 min-w-0">
        {event.flyer_url && (
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-sm overflow-hidden bg-slate-100 flex-shrink-0">
            <Img variant="event" src={resolveImg(event.flyer_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <h3 className="text-[13px] sm:text-sm font-bold text-[#002249] line-clamp-2 leading-tight">{event.title}</h3>
            {event.is_featured && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            {showRecapBadge && event.recap_published && <span className="text-[9px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Recap Published</span>}
            {hasRecapDraft && !event.recap_published && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-0.5"><FileText className="w-2.5 h-2.5" />Draft</span>}
            {event.payment_link && <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{event.payment_platform || 'Payment'}</span>}
          </div>
          <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap mt-1.5 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{event.date}</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{event.time}</span>
            <span className="flex items-center gap-1 min-w-0"><MapPin className="w-3 h-3 flex-shrink-0" /><span className="truncate">{event.location}</span></span>
          </div>
          {event.max_capacity && <p className="text-[10px] text-slate-400 mt-1">Capacity: {event.max_capacity} · RSVP: {event.rsvp_open ? 'Open' : 'Closed'}</p>}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-wrap flex-shrink-0 pl-0 sm:pl-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
        {(tab === 'rsvp' || tab === 'payments') && (
          <Button variant="outline" size="sm" onClick={() => loadRegistrations(event.id)} className="rounded-sm text-[10px] h-8 px-2.5" data-testid={`view-rsvp-${event.id}`}>
            <Users className="w-3 h-3 mr-1" />RSVP
          </Button>
        )}
        {isPast && !hasRecapDraft && (
          <Button variant="outline" size="sm" onClick={() => handleGenerateRecap(event.id)} className="rounded-sm text-[10px] h-8 px-2.5 border-[#002249]/30 text-[#002249] hover:bg-[#002249]/5" data-testid={`generate-recap-${event.id}`}>
            <Sparkles className="w-3 h-3 mr-1" />{autoPublishRecaps ? 'Generate & Publish' : 'Generate Recap'}
          </Button>
        )}
        {hasRecapDraft && !event.recap_published && (
          <Button variant="outline" size="sm" onClick={() => handlePublishRecap(event.id, true)} className="rounded-sm text-[10px] h-8 px-2.5 border-emerald-400 text-emerald-700 hover:bg-emerald-50 font-semibold" data-testid={`publish-recap-${event.id}`}>
            <CheckCircle2 className="w-3 h-3 mr-1" />Publish Recap
          </Button>
        )}
        {hasRecapDraft && event.recap_published && (
          <Button variant="outline" size="sm" onClick={() => handlePublishRecap(event.id, false)} className="rounded-sm text-[10px] h-8 px-2.5 border-slate-300 text-slate-600 hover:bg-slate-50" data-testid={`unpublish-recap-${event.id}`}>
            <XCircle className="w-3 h-3 mr-1" />Unpublish
          </Button>
        )}
        {hasRecapDraft && (
          <a
            href={`/admin/blog`}
            className="inline-flex items-center gap-1 rounded-sm text-[10px] h-8 px-2.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-medium"
            data-testid={`view-recap-draft-${event.id}`}
          >
            <Newspaper className="w-3 h-3" />Edit
          </a>
        )}
        <Button variant="ghost" size="icon" onClick={() => toggleNewsletterOptIn(event)} className={`h-8 w-8 ${event.include_in_newsletter ? 'text-[#E63946] hover:text-[#c02a35]' : 'text-slate-400 hover:text-[#E63946]'}`} title={event.include_in_newsletter ? 'In newsletter pipeline — click to remove' : 'Add to newsletter pipeline'} data-testid={`newsletter-toggle-event-${event.id}`}>
          <Mail className={`w-4 h-4 ${event.include_in_newsletter ? 'fill-[#E63946]/15' : ''}`} />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => openEditor(event)} className="text-slate-400 hover:text-[#002249] h-8 w-8" data-testid={`edit-event-${event.id}`}>
          <Edit className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => handleDelete(event.id)} className="text-red-400 hover:text-red-600 h-8 w-8" data-testid={`delete-event-${event.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="events-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#002249]">Events Management</h1>
          <p className="text-[13px] text-slate-500 mt-1">Full event lifecycle: create, manage, track, recap</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImport(true)}
            className="rounded-sm border-[#002249]/30 text-[#002249] hover:bg-[#002249]/5 flex-1 sm:flex-none h-10"
            data-testid="import-pdf-btn"
          >
            <FileUp className="w-4 h-4 mr-1.5" /> <span className="hidden xs:inline">Import from </span>PDF
          </Button>
          <Button onClick={() => openEditor()} size="sm" className="bg-[#002249] hover:bg-[#001833] text-white rounded-sm flex-1 sm:flex-none h-10" data-testid="create-event-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Event
          </Button>
        </div>
      </div>

      <EventPDFImportDialog
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => {
          fetchEvents();
          ensureRecapDrafts();
        }}
      />

      {/* ── Tabs ── */}
      <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" data-testid="events-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const count = t.key === 'upcoming' ? upcomingEvents.length : t.key === 'past' ? pastEvents.length : t.key === 'recaps' ? recapEvents.length : null;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); setSelectedEventId(null); }}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-[12px] font-semibold whitespace-nowrap transition-colors flex-shrink-0 ${tab === t.key ? 'bg-[#002249] text-white shadow-sm' : 'text-slate-500 hover:bg-slate-100 border border-slate-200'}`}
              data-testid={`tab-${t.key}`}>
              <Icon className="w-3.5 h-3.5" />{t.label}{count !== null && <span className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${tab === t.key ? 'bg-white/20' : 'bg-slate-100'}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      {/* ══════════ TAB CONTENT ══════════ */}

      {/* Upcoming / Past Events */}
      {(tab === 'upcoming' || tab === 'past') && (
        <div className="space-y-3">
          {(tab === 'upcoming' ? upcomingEvents : pastEvents).map(e => <EventCard key={e.id} event={e} />)}
          {(tab === 'upcoming' ? upcomingEvents : pastEvents).length === 0 && (
            <div className="text-center py-16 bg-slate-50 rounded-sm border border-dashed border-slate-200">
              <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">No {tab} events yet</p>
              <p className="text-xs text-slate-400 mt-1">
                {tab === 'upcoming' ? 'Click "New Event" or "Import from PDF" to add events.' : 'Past events appear here automatically.'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Event Recaps */}
      {tab === 'recaps' && (
        <div className="space-y-3">
          {/* Consolidated recap pipeline banner (approval-bypass + auto-publish toggle + refresh) */}
          <div className="bg-gradient-to-br from-blue-50 to-emerald-50 border border-blue-200 rounded-sm p-4 mb-4" data-testid="recap-info-banner">
            <div className="flex items-start gap-2.5 mb-3">
              <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-[#002249]">Event Recap Pipeline</p>
                <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">
                  Events Team owns recap content — <strong>does not require Communications approval</strong>. Use <strong>Publish Recap</strong> on any past-event card below, or enable <strong>Auto-publish</strong> to skip drafts entirely.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-blue-200/60">
              <label className="flex items-center gap-2 px-3 py-1.5 bg-white border border-blue-300 rounded-sm cursor-pointer hover:bg-blue-50 transition-colors" data-testid="auto-publish-toggle-label">
                <Switch
                  checked={autoPublishRecaps}
                  onCheckedChange={toggleAutoPublish}
                  data-testid="auto-publish-toggle"
                />
                <span className="text-[12px] font-semibold text-blue-900">Auto-publish</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${autoPublishRecaps ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{autoPublishRecaps ? 'ON' : 'OFF'}</span>
              </label>
              <Button size="sm" variant="outline" onClick={ensureRecapDrafts} className="rounded-sm text-[11px] h-8 border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="refresh-recap-drafts">
                <Sparkles className="w-3 h-3 mr-1" />Refresh Drafts
              </Button>
            </div>
          </div>
          {pastEvents.map(e => <EventCard key={e.id} event={e} showRecapBadge />)}
          {pastEvents.length === 0 && (
            <div className="text-center py-16 bg-slate-50 rounded-sm border border-dashed border-slate-200">
              <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">No past events to recap yet</p>
              <p className="text-xs text-slate-400 mt-1">Recaps are auto-drafted when an event's date passes.</p>
            </div>
          )}
        </div>
      )}

      {/* RSVP / Attendance */}
      {tab === 'rsvp' && (
        <div>
          {!selectedEventId ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-500 mb-3">Select an event to view RSVP and attendance data:</p>
              {events.map(e => <EventCard key={e.id} event={e} />)}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Button variant="outline" size="sm" onClick={() => setSelectedEventId(null)} className="rounded-sm">
                  <X className="w-3.5 h-3.5 mr-1" />Back
                </Button>
                <h2 className="text-lg font-bold text-[#002249]">{events.find(e => e.id === selectedEventId)?.title}</h2>
              </div>

              {/* Stats cards */}
              {eventStats && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="event-stats">
                  {[
                    { label: 'Registered', value: eventStats.total_registered, icon: Users, color: 'text-blue-700 bg-blue-50' },
                    { label: 'Attended', value: eventStats.total_attended, icon: CheckCircle2, color: 'text-green-700 bg-green-50' },
                    { label: 'Paid', value: eventStats.total_paid, icon: DollarSign, color: 'text-emerald-700 bg-emerald-50' },
                    { label: 'Pending Pay', value: eventStats.total_pending_payment, icon: Clock, color: 'text-amber-700 bg-amber-50' },
                    { label: 'Spots Left', value: eventStats.spots_remaining ?? 'Unlimited', icon: BarChart3, color: 'text-purple-700 bg-purple-50' },
                  ].map(s => {
                    const SIcon = s.icon;
                    return (
                      <div key={s.label} className={`rounded-sm p-3 ${s.color}`}>
                        <SIcon className="w-4 h-4 mb-1" />
                        <p className="text-xl font-bold">{s.value}</p>
                        <p className="text-[10px] font-medium uppercase tracking-wide opacity-70">{s.label}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Registrations table */}
              <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="registrations-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Name</th>
                      <th className="text-left p-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Email</th>
                      <th className="text-left p-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Registered</th>
                      <th className="text-center p-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Attended</th>
                      <th className="text-center p-3 font-bold text-slate-500 uppercase tracking-wider text-[10px]">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((reg, idx) => (
                      <tr key={reg.id} className="border-t border-slate-100" data-testid={`reg-row-${idx}`}>
                        <td className="p-3 font-medium text-[#002249]">{reg.user_name}</td>
                        <td className="p-3 text-slate-500">{reg.user_email}</td>
                        <td className="p-3 text-slate-400">{new Date(reg.registered_at).toLocaleDateString()}</td>
                        <td className="p-3 text-center">
                          <button onClick={() => toggleAttendance(selectedEventId, reg.id, !reg.attended)}
                            className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${reg.attended ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-400 hover:bg-green-200'}`}
                            data-testid={`toggle-attended-${idx}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <select value={reg.payment_status || 'pending'} onChange={e => updatePaymentStatus(selectedEventId, reg.id, e.target.value)}
                            className="text-[10px] border border-slate-200 rounded-sm p-1" data-testid={`payment-status-${idx}`}>
                            <option value="pending">Pending</option>
                            <option value="external">External</option>
                            <option value="paid">Paid</option>
                            <option value="refunded">Refunded</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                    {registrations.length === 0 && (
                      <tr><td colSpan={5} className="p-8 text-center text-slate-400">No registrations yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payments / Sales */}
      {tab === 'payments' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500 mb-3">Events with payment links configured:</p>
          {events.filter(e => e.payment_link).map(e => <EventCard key={e.id} event={e} />)}
          {events.filter(e => e.payment_link).length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No events with payment links configured yet</p>
              <p className="text-xs mt-1">Edit an event to add a Zeffy, Square, or PayPal payment link</p>
            </div>
          )}
        </div>
      )}

      {/* Media Gallery */}
      {tab === 'gallery' && (
        <div className="space-y-6">
          {events.filter(e => (e.gallery_images?.length || 0) > 0 || e.flyer_url).map(e => (
            <div key={e.id} className="bg-white border border-slate-200 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-3">
                <Camera className="w-4 h-4 text-[#002249]" />
                <h3 className="text-sm font-bold text-[#002249]">{e.title}</h3>
                <span className="text-[10px] text-slate-400">{e.date}</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
                {e.flyer_url && (
                  <div className="aspect-square rounded-sm overflow-hidden bg-slate-100">
                    <Img variant="event" src={resolveImg(e.flyer_url)} alt="Flyer" className="w-full h-full object-cover" />
                  </div>
                )}
                {(e.gallery_images || []).map((img, i) => (
                  <div key={i} className="aspect-square rounded-sm overflow-hidden bg-slate-100">
                    <Img variant="generic" src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {events.filter(e => (e.gallery_images?.length || 0) > 0 || e.flyer_url).length === 0 && (
            <div className="text-center py-16 text-slate-400">
              <Image className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No event media yet</p>
            </div>
          )}
        </div>
      )}

      {/* ══════════ EVENT EDITOR MODAL ══════════ */}
      <AdminModal
        open={showEditor}
        onClose={closeEditor}
        size="xl"
        testId="event-editor-modal"
        closeOnBackdrop={false}
        title={editingEvent ? 'Edit Event' : 'Create Event'}
        headerExtra={dirty && (
          <span className="text-[11px] font-medium text-amber-600 flex items-center gap-1" data-testid="event-dirty-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Unsaved · auto-saved
          </span>
        )}
        footer={(
          <>
            <Button variant="outline" onClick={closeEditor} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#002249] hover:bg-[#001833] text-white rounded-sm" data-testid="save-event-btn">
              <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Event'}
            </Button>
          </>
        )}
      >
        {/* Draft restore banner */}
        {restoredDraft?.data && initialSnapshot &&
          JSON.stringify(restoredDraft.data) !== JSON.stringify(initialSnapshot) &&
          JSON.stringify(restoredDraft.data) !== JSON.stringify(formData) && (
          <div
            className="mb-5 -mx-5 sm:-mx-6 px-5 sm:px-6 py-3 bg-[#002249]/5 border-y border-[#002249]/15 flex items-center justify-between gap-3"
            data-testid="event-draft-restore-banner"
          >
            <span className="text-[13px] text-[#002249] font-medium flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Unsaved draft detected from a previous session.
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={clearDraft} className="h-8" data-testid="event-discard-draft">Discard</Button>
              <Button size="sm" onClick={restoreDraftToForm} className="h-8 bg-[#002249]" data-testid="event-restore-draft">Restore</Button>
            </div>
          </div>
        )}

        <div className="space-y-5">
                {/* Details */}
                <div>
                  <h3 className="text-xs font-bold text-[#002249] uppercase tracking-wide mb-3 flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />Event Details</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Title *</Label>
                      <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} className="rounded-sm" data-testid="event-title" />
                    </div>
                    <div><Label className="text-xs">Start Date *</Label><Input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} className="rounded-sm" data-testid="event-date" /></div>
                    <div><Label className="text-xs">Start Time *</Label><Input value={formData.time} onChange={e => setFormData({ ...formData, time: e.target.value })} className="rounded-sm" placeholder="6:00 PM" data-testid="event-time" /></div>
                    <div>
                      <Label className="text-xs">End Date <span className="text-slate-400 font-normal">(optional — for multi-day events)</span></Label>
                      <Input type="date" value={formData.end_date} onChange={e => setFormData({ ...formData, end_date: e.target.value })} min={formData.date || undefined} className="rounded-sm" data-testid="event-end-date" />
                    </div>
                    <div>
                      <Label className="text-xs">End Time <span className="text-slate-400 font-normal">(optional)</span></Label>
                      <Input value={formData.end_time} onChange={e => setFormData({ ...formData, end_time: e.target.value })} className="rounded-sm" placeholder="9:00 PM" data-testid="event-end-time" />
                    </div>
                    <div className="sm:col-span-2"><Label className="text-xs">Location *</Label><Input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} className="rounded-sm" data-testid="event-location" /></div>
                    <div className="sm:col-span-2"><Label className="text-xs">Description</Label><textarea value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className={`${inputClass} min-h-[80px]`} data-testid="event-description" /></div>
                    <div><Label className="text-xs">Category</Label>
                      <select value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className={inputClass}>
                        <option value="general">General</option><option value="networking">Networking</option><option value="gala">Gala</option><option value="workshop">Workshop</option><option value="conference">Conference</option><option value="social">Social</option>
                      </select>
                    </div>
                    <div><Label className="text-xs">Max Capacity</Label><Input type="number" value={formData.max_capacity} onChange={e => setFormData({ ...formData, max_capacity: e.target.value })} className="rounded-sm" placeholder="Leave empty for unlimited" data-testid="event-capacity" /></div>
                  </div>
                  <div className="flex items-center gap-6 mt-3 flex-wrap">
                    <label className="flex items-center gap-2 text-xs"><Switch checked={formData.is_featured} onCheckedChange={v => setFormData({ ...formData, is_featured: v })} /> Featured</label>
                    <label className="flex items-center gap-2 text-xs"><Switch checked={formData.rsvp_open} onCheckedChange={v => setFormData({ ...formData, rsvp_open: v })} data-testid="event-rsvp-toggle" /> RSVP Open</label>
                    <label className="flex items-center gap-2 text-xs"><Switch checked={formData.is_member_event} onCheckedChange={v => setFormData({ ...formData, is_member_event: v })} /> Members Only</label>
                    <label className="flex items-center gap-2 text-xs"><Switch checked={!!formData.is_external} onCheckedChange={v => setFormData({ ...formData, is_external: v })} data-testid="event-external-toggle" /> External Event (Recommended)</label>
                  </div>
                </div>

                {/* Payment Link */}
                <div>
                  <h3 className="text-xs font-bold text-[#002249] uppercase tracking-wide mb-3 flex items-center gap-2"><DollarSign className="w-3.5 h-3.5" />Payment / Tickets</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Payment Platform</Label>
                      <select value={formData.payment_platform} onChange={e => setFormData({ ...formData, payment_platform: e.target.value })} className={inputClass} data-testid="event-payment-platform">
                        {PAYMENT_PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    </div>
                    <div><Label className="text-xs">Payment Link (URL)</Label><Input value={formData.payment_link} onChange={e => setFormData({ ...formData, payment_link: e.target.value })} className="rounded-sm" placeholder="https://..." data-testid="event-payment-link" /></div>
                  </div>
                </div>

                {/* External Links — Feb 2026 */}
                <div>
                  <h3 className="text-xs font-bold text-[#002249] uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Link2 className="w-3.5 h-3.5" />External Links
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Ticket URL <span className="text-slate-400 font-normal">(Buy Tickets)</span></Label>
                      <Input value={formData.ticket_url} onChange={e => setFormData({ ...formData, ticket_url: e.target.value })} className="rounded-sm" placeholder="https://eventbrite.com/..." data-testid="event-ticket-url" />
                    </div>
                    <div>
                      <Label className="text-xs">Info URL <span className="text-slate-400 font-normal">(Learn More)</span></Label>
                      <Input value={formData.info_url} onChange={e => setFormData({ ...formData, info_url: e.target.value })} className="rounded-sm" placeholder="https://..." data-testid="event-info-url" />
                    </div>
                    <div>
                      <Label className="text-xs">Video URL <span className="text-slate-400 font-normal">(YouTube / Instagram / Vimeo)</span></Label>
                      <Input value={formData.video_url} onChange={e => setFormData({ ...formData, video_url: e.target.value })} className="rounded-sm" placeholder="https://youtu.be/..." data-testid="event-video-url" />
                    </div>
                    <div>
                      <Label className="text-xs">External Page URL <span className="text-slate-400 font-normal">(generic)</span></Label>
                      <Input value={formData.external_url} onChange={e => setFormData({ ...formData, external_url: e.target.value })} className="rounded-sm" placeholder="https://..." data-testid="event-external-url" />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">These buttons appear on the public Event Detail Page.</p>
                </div>

                {/* Media */}
                {editingEvent && (
                  <div>
                    <h3 className="text-xs font-bold text-[#002249] uppercase tracking-wide mb-3 flex items-center gap-2"><Camera className="w-3.5 h-3.5" />Media</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs mb-2 block">Flyer</Label>
                        {formData.flyer_url && <Img variant="event" src={resolveImg(formData.flyer_url)} alt="Flyer" className="w-full max-h-40 object-contain mb-2 rounded-sm border" />}
                        <label className="inline-flex items-center gap-1 text-xs text-[#002249] cursor-pointer hover:text-[#E63946] transition-colors">
                          <Upload className="w-3.5 h-3.5" />{uploading.flyer ? 'Uploading...' : 'Upload Flyer'}
                          <input type="file" accept="image/*" className="hidden" onChange={e => uploadImage(editingEvent.id, 'flyer', e.target.files[0])} />
                        </label>
                      </div>
                      <div>
                        <Label className="text-xs mb-2 block">Cover Image</Label>
                        {formData.image_url && <Img variant="generic" src={resolveImg(formData.image_url)} alt="Cover" className="w-full max-h-40 object-contain mb-2 rounded-sm border" />}
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="inline-flex items-center gap-1 text-xs text-[#002249] cursor-pointer hover:text-[#E63946] transition-colors">
                            <Upload className="w-3.5 h-3.5" />{uploading.cover ? 'Uploading...' : 'Upload Cover'}
                            <input type="file" accept="image/*" className="hidden" onChange={e => uploadImage(editingEvent.id, 'cover', e.target.files[0])} />
                          </label>
                          <button
                            type="button"
                            onClick={() => setAiImageOpen(true)}
                            className="inline-flex items-center gap-1 text-xs text-[#002249] hover:text-[#E63946] transition-colors font-semibold"
                            data-testid="ai-image-open-event"
                          >
                            <Sparkles className="w-3.5 h-3.5" /> Generate with AI
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Gallery */}
                    <div className="mt-4">
                      <Label className="text-xs mb-2 block">Gallery ({(formData.gallery_images || []).length} images)</Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(formData.gallery_images || []).map((img, i) => (
                          <div key={i} className="w-16 h-16 rounded-sm overflow-hidden bg-slate-100 relative">
                            <Img variant="generic" src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                      <label className="inline-flex items-center gap-1 text-xs text-[#002249] cursor-pointer hover:text-[#E63946]">
                        <Upload className="w-3.5 h-3.5" />{uploading.gallery ? 'Uploading...' : 'Add Gallery Image'}
                        <input type="file" accept="image/*" className="hidden" onChange={e => uploadImage(editingEvent.id, 'gallery', e.target.files[0])} />
                      </label>
                    </div>
                  </div>
                )}

                {/* Recap */}
                <div>
                  <h3 className="text-xs font-bold text-[#002249] uppercase tracking-wide mb-3 flex items-center gap-2"><FileText className="w-3.5 h-3.5" />Event Recap</h3>
                  <div className="space-y-3">
                    <div><Label className="text-xs">Recap Summary (short)</Label><textarea value={formData.recap_summary} onChange={e => setFormData({ ...formData, recap_summary: e.target.value })} className={`${inputClass} min-h-[60px]`} placeholder="Brief summary of the event..." data-testid="event-recap-summary" /></div>
                    <div><Label className="text-xs">Full Recap (detailed)</Label><textarea value={formData.recap} onChange={e => setFormData({ ...formData, recap: e.target.value })} className={`${inputClass} min-h-[100px]`} placeholder="Detailed event recap..." data-testid="event-recap" /></div>
                    <div><Label className="text-xs">YouTube Video Link</Label><Input value={formData.recap_youtube_url} onChange={e => setFormData({ ...formData, recap_youtube_url: e.target.value })} className="rounded-sm" placeholder="https://youtube.com/watch?v=..." data-testid="event-youtube" /></div>
                    <label className="flex items-center gap-2 text-xs"><Switch checked={formData.recap_published} onCheckedChange={v => setFormData({ ...formData, recap_published: v })} data-testid="event-recap-published" /> Publish Recap</label>
                  </div>
                </div>
        </div>
      </AdminModal>

      {aiImageOpen && (
        <AIImageGenerateModal
          contextType="event"
          contextId={editingEvent?.id || null}
          contextData={{
            title: formData.title,
            description: formData.description,
            date: formData.date,
            location: formData.location,
            event_type: formData.category,
          }}
          contextLabel={formData.title || 'New event'}
          existingImageUrl={formData.image_url || null}
          onImageSelected={(url) => {
            setFormData((prev) => ({ ...prev, image_url: url }));
            toast.success('AI image attached to event');
          }}
          onClose={() => setAiImageOpen(false)}
        />
      )}
    </div>
  );
};

export default EventsManagementPage;
